import { ArrowLeft, Download, X, Sparkles, Loader2 } from 'lucide-react'
import type { CompetitorBrief, BriefScope, Competitor } from '@ispot/shared'
import { useAIStore } from '@/stores/aiStore'
import { BriefSection } from './BriefSection'

interface CompetitorBriefPanelProps {
  brief: CompetitorBrief | null
  scope: BriefScope | null
  competitors: Competitor[]
  isLoading: boolean
  onClose: () => void
  onGenerate: (scope: BriefScope, apiKey: string) => Promise<void>
  onEnrich?: (competitorId: string, apiKey: string) => Promise<void>
}

export function CompetitorBriefPanel({
  brief,
  scope,
  competitors,
  isLoading,
  onClose,
  onGenerate,
  onEnrich,
}: CompetitorBriefPanelProps) {
  const apiKey = useAIStore(state => state.apiKey)

  if (!scope) return null

  const scopeLabel = scope.type === 'competitor'
    ? competitors.find(c => c.id === scope.competitorId)?.name ?? 'Competitor'
    : scope.category

  const categoryCompetitors = scope.type === 'category'
    ? competitors.filter(c => c.category === scope.category)
    : competitors.filter(c => c.id === (scope as { competitorId: string }).competitorId)

  const targetCompetitor = scope.type === 'competitor'
    ? competitors.find(c => c.id === scope.competitorId)
    : null
  const isCompetitorScope = scope.type === 'competitor'
  const isEnriching = !!targetCompetitor && targetCompetitor.enrichmentStatus === 'enriching'

  function handlePrimaryAction() {
    if (!apiKey) return
    // For competitor scope, re-enrich = scrape + brief + derive fields.
    // For category scope, fall back to brief-only regeneration (category has no enrichment pipeline).
    if (isCompetitorScope && onEnrich && scope.type === 'competitor') {
      onEnrich(scope.competitorId, apiKey)
    } else {
      onGenerate(scope, apiKey)
    }
  }

  function handleDownload() {
    if (!brief) return
    const text = [
      `# Competitor Brief: ${scopeLabel}`,
      '',
      '## Summary',
      brief.content.summary,
      '',
      ...(brief.content.pricing
        ? ['## Pricing', brief.content.pricing, '']
        : []),
      '## Strengths',
      ...brief.content.strengths.map(s => `- ${s}`),
      '',
      '## Weaknesses',
      ...brief.content.weaknesses.map(w => `- ${w}`),
      '',
      '## Key Insights',
      ...brief.content.insights.map(i => `- ${i}`),
      '',
      `_Generated: ${new Date(brief.generatedAt).toLocaleString()}_`,
    ].join('\n')
    const blob = new Blob([text], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `competitor-brief-${scopeLabel?.toLowerCase().replace(/\s+/g, '-')}.md`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{
      width: '460px',
      flexShrink: 0,
      borderLeft: '1px solid var(--color-border)',
      background: 'var(--color-warm-50)',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: 'white',
      }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center' }}>
          <ArrowLeft size={16} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
            Competitive Brief
          </p>
          <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {scopeLabel}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {categoryCompetitors.length > 0 && (
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '20px',
              background: 'var(--color-accent)',
              color: 'white',
              fontWeight: 600,
            }}>
              {categoryCompetitors.length} co.
            </span>
          )}
          {brief && (
            <button onClick={handleDownload} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center' }}>
              <Download size={15} />
            </button>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center' }}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
        {isLoading || isEnriching ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', gap: '12px', color: 'var(--color-text-muted)' }}>
            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ fontSize: '13px' }}>{isEnriching ? 'Enriching competitor…' : 'Generating competitive brief…'}</p>
          </div>
        ) : brief ? (
          <>
            {brief.content.summary && (
              <div style={{ padding: '14px', background: 'white', borderRadius: '10px', border: '1px solid var(--color-border)', marginBottom: '20px' }}>
                <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: '6px' }}>Summary</p>
                <p style={{ fontSize: '13px', color: 'var(--color-text)', lineHeight: 1.6 }}>{brief.content.summary}</p>
              </div>
            )}
            {brief.content.pricing && (
              <div style={{ padding: '14px', background: 'white', borderRadius: '10px', border: '1px solid var(--color-border)', marginBottom: '20px' }}>
                <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: '6px' }}>Pricing</p>
                <p style={{ fontSize: '13px', color: 'var(--color-text)', lineHeight: 1.6 }}>{brief.content.pricing}</p>
              </div>
            )}
            <BriefSection
              title="Strengths"
              items={brief.content.strengths}
              dotColor="var(--color-accent)"
            />
            <BriefSection
              title="Weaknesses"
              items={brief.content.weaknesses}
              dotColor="var(--color-error, #dc3545)"
            />
            <BriefSection
              title="Key Insights"
              items={brief.content.insights}
              dotColor="var(--color-business)"
              bgColor="var(--color-warm-100)"
            />
            <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '16px' }}>
              Generated {new Date(brief.generatedAt).toLocaleString()}
              {brief.modelUsed && ` · ${brief.modelUsed}`}
            </p>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', gap: '16px', textAlign: 'center' }}>
            <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
              No brief yet for <strong>{scopeLabel}</strong>.<br />
              {isCompetitorScope ? 'Run enrichment to populate it.' : 'Generate one with AI.'}
            </p>
            <button
              onClick={handlePrimaryAction}
              style={{
                padding: '8px 18px',
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Sparkles size={14} />
              {isCompetitorScope ? 'Re-enrich' : 'Generate Brief'}
            </button>
          </div>
        )}
      </div>

      {brief && (
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--color-border)', background: 'white' }}>
          <button
            onClick={handlePrimaryAction}
            disabled={isLoading || isEnriching}
            style={{
              width: '100%',
              padding: '8px',
              background: 'none',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--color-text-secondary)',
              cursor: isLoading || isEnriching ? 'default' : 'pointer',
              opacity: isLoading || isEnriching ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <Sparkles size={13} />
            {isCompetitorScope ? 'Re-enrich' : 'Regenerate'}
          </button>
        </div>
      )}
    </div>
  )
}
